import { useCallback, useRef } from "react";

import { ORG_GEO_FIELDS, ORG_GEO_SIMS } from "constants/settings.constants";

import { useUser } from "hooks";
import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";

import useFilteredGeoCalculationsJobs from "components/org-settings/hooks/useFilteredGeoCalculationsJobs";
import { useGeoSticksCalculationJobs } from "components/org-settings/hooks/useGeoSticksCalculationJobs";
import { useGetGeoCalculationsJobs } from "components/org-settings/hooks/useGetGeoCalculationsJobs";
import { GeoModelJobStatus } from "components/org-settings/models/GeoModelJob";
import { isInProgressStatus } from "components/org-settings/utils/jobStatusTaxonomy";

/** The two groups whose subgroups are calculation job names. */
export const GEO_GROUPS: string[] = [ORG_GEO_FIELDS, ORG_GEO_SIMS];

export function isGeoGroup(group: string): boolean {
  return GEO_GROUPS.includes(group);
}

/** The parts of a geo calc / geo sim job both job lists report the same way. */
interface GeoJobLike {
  name?: string;
  modelSource?: string;
  plays?: string[];
  status?: string;
  lastSuccessfulRunDate?: unknown;
}

/**
 * Statuses that name a run which has not produced values. Terminal ones
 * included: a job that ended in an error and has no successful run behind it
 * has never written anything.
 */
const NEVER_RAN_STATUSES: readonly string[] = [
  GeoModelJobStatus.NotStarted,
  GeoModelJobStatus.Error,
  GeoModelJobStatus.Cancelled
];

/**
 * The two lists carry this date in different shapes: the simulation job sends
 * a Date, and the calculation job sends Mongo's extended JSON wrapper, which
 * is an object whether or not it holds a time. Unwrapped the way the jobs
 * table does it, so an empty wrapper reads as "never ran" rather than as a
 * truthy object.
 */
function hasSuccessfulRun(job: GeoJobLike): boolean {
  const runDate = job.lastSuccessfulRunDate;
  if (!runDate) return false;
  if (typeof runDate === "object" && "$date" in runDate) {
    return !!(runDate as { $date?: { $numberLong?: string } }).$date?.$numberLong;
  }
  return true;
}

/**
 * Has this job ever written values?
 *
 * `lastSuccessfulRunDate` is what settles it — the field both jobs tables read
 * for their "last successful run" column. A live status does not: the worker
 * writes progress as the status itself ("Started", "40/128"), and a job
 * re-running now still has the previous run's values to show.
 *
 * With no date, only a status that names an unfinished or failed run says
 * there is nothing behind it. Anything unrecognised counts as having data:
 * withholding a fetch is only safe when we are sure there is nothing to fetch.
 */
export function geoJobHasData(job: Maybe<GeoJobLike>): boolean {
  if (!job) return true;
  if (hasSuccessfulRun(job)) return true;
  const status = job.status;
  return !status || !(isInProgressStatus(status) || NEVER_RAN_STATUSES.includes(status));
}

function namesWithoutData(jobs: Maybe<GeoJobLike[]>): Set<string> {
  return new Set(
    (jobs ?? []).filter((job) => job.name && !geoJobHasData(job)).map((job) => job.name)
  );
}

function allNames(jobs: Maybe<GeoJobLike[]>): Set<string> {
  return new Set((jobs ?? []).filter((job) => job.name).map((job) => job.name));
}

export interface GeoJobReadiness {
  /**
   * Both job lists have answered, so the unready names below are the whole
   * answer. Until then a geo group must not be asked for: its columns would be
   * frozen against a list that says every job is ready.
   */
  isSettled: boolean;
  /**
   * Per geo group, the job names whose job has never produced data. A geo
   * column's subgroup IS its job name (the focus panel matches them the same
   * way, see `OrgGeoCategory.tsx`), so this is what tells the ticket which
   * columns have nothing behind them.
   */
  unreadyJobNames: Record<string, Set<string>>;
  /**
   * Per geo group, every job name these lists hold that this reader may be
   * offered (a private model they have access to, or an entitled EVA Research
   * job). The ticket's
   * headings come from the field catalogue, which keeps a column for every job
   * that has ever written one — which is why it listed jobs no other surface
   * shows. Matching these names is what makes it agree with the Data Table: a
   * job that has been deleted, or that a lapsed tier no longer covers, is not
   * in them, so the ticket stops listing it.
   *
   * This decides what the ticket lists, not what the service will answer. The
   * well-detail endpoint resolves whatever fields it is asked for, so none of
   * this protects anything and no later reader should treat it as though it
   * does. A private model is checked against the caller's own `/sources/private`,
   * the same filter every other geo job surface applies, so a reader off a
   * model's access list does not see its fields.
   *
   * A group is absent only until its list has answered. Once it has, an empty
   * set means this reader may see none of the jobs: the server omits the
   * EVA Research jobs of a lapsed org. A failed first request is not an answer,
   * so the lists stay unsettled and nothing is frozen until a fetch succeeds.
   *
   * Scoped by the job itself, never by a project: see the comment on the
   * simulation list below.
   */
  knownJobNames: Partial<Record<string, Set<string>>>;
  /**
   * A job list has failed with no earlier answer to fall back on. The lists
   * stay unsettled, so no geo group is asked for; this is what lets the card say
   * so instead of spinning for ever.
   */
  isError: boolean;
  /** Refetches whichever list failed (job lists or access list), background failures included. Others are left alone. */
  retry: () => void;
}

/**
 * Worth knowing before fetching: the service resolves a geo column one at a
 * time and answers "n/a" for every field of an unfinished job, so asking is
 * minutes of work for a tab of empty rows.
 *
 * @param enabled whether this ticket has any geo columns at all. The ticket
 * opens for every user, and both lists are the whole org's jobs — a ticket
 * with no geo tab must not ask for them.
 *
 * Reads the two job lists and the caller's own `/sources/private`. The org-wide
 * `/sources/admin` list has no server role check and the ticket renders for every
 * role, so it is never touched here, directly or through a dependency.
 */
export function useGeoJobReadiness(enabled: boolean): GeoJobReadiness {
  // The org-settings hooks own these two queries, so the ticket borrows them
  // rather than registering a second copy of each key. No poll: the ticket
  // freezes its answer when a group is first asked for, so a later refetch
  // would only cost every open ticket a request.
  const calcQuery = useGetGeoCalculationsJobs({ enabled });
  // No scenario ids on purpose, which is what the two other readers of this
  // list pass. Both of their scenario lists are scoped — the focus selector's
  // to the open project, the settings table's to the projects one user happens
  // to be able to see, with a per-project request failure swallowed as an empty
  // list. Either would drop a job whose values are on the well because of which
  // project is open, or which projects the reader has, and neither says
  // anything about the job. What is left says something about the job itself:
  // whether these lists still hold it, and whether the reader's tier covers
  // it. The selector and the settings table keep their own narrowing.
  const simQuery = useGeoSticksCalculationJobs(undefined, { enabled, poll: false });
  const accessQuery = usePlayTo3DDataPrivateSources(enabled);
  const hasUser = !!useUser()?.user;
  const queriesRef = useRef({ calcQuery, simQuery, accessQuery });
  queriesRef.current = { calcQuery, simQuery, accessQuery };
  const retry = useCallback(() => {
    const { calcQuery: calc, simQuery: sim, accessQuery: access } = queriesRef.current;
    if (calc.isError) void calc.refetch();
    if (sim.isError) void sim.refetch();
    if (access.isError) void access.refetch();
  }, []);
  // Entitlement and per-user private-model access in one place, shared with the
  // other geo job surfaces. Withholds everything until the access list answers.
  const calcJobs = useFilteredGeoCalculationsJobs(calcQuery.data, enabled);
  const simJobs = useFilteredGeoCalculationsJobs(simQuery.data, enabled);

  // Referential stability guard, the same one `useWellDetailGroupQuery` runs
  // and for the same reason: `WellCard`'s tab effect depends on this value and
  // calls `setTabs`, so a new object every render is a self-sustaining update
  // loop (React #185). Compared by content, not by the query data's identity,
  // so the guard does not rest on react-query handing back the same array.
  const resultRef = useRef<Maybe<GeoJobReadiness>>(null);
  const keyRef = useRef<Maybe<string>>(null);

  // A null user only occurs during sign-in/out, and carries no modules, so
  // settling then would freeze every EVA Research job out of the card.
  // A query that was never enabled reports no loading of its own, which is
  // what settled means for it: there is nothing to wait for. A list that
  // failed with nothing to show is not settled: the card freezes this answer
  // for its whole life, so freezing it would hide every job until the card is
  // reopened. One that failed on a background refetch still holds its earlier
  // answer (react-query keeps `data`), which is as good as it was before the
  // failure, so a dropdown opened afterwards must not be turned away. The
  // access list is held to the same rule, or a failed first lookup would
  // freeze every private model's fields out.
  const calcFailed = !!calcQuery.isError && !calcQuery.data;
  const simFailed = !!simQuery.isError && !simQuery.data;
  const accessFailed = !!accessQuery.isError && !accessQuery.data;
  const isSettled =
    hasUser &&
    !calcQuery.isLoading &&
    !simQuery.isLoading &&
    !accessQuery.isLoading &&
    !calcFailed &&
    !simFailed &&
    !accessFailed;

  // Absent until a list has answered; empty once it has answered with nothing
  // this reader may be offered, so a consumer never mistakes "not yet" for "none".
  const knownJobNames: GeoJobReadiness["knownJobNames"] = {};
  if (isSettled) {
    if (calcQuery.data) knownJobNames[ORG_GEO_FIELDS] = allNames(calcJobs);
    if (simQuery.data) knownJobNames[ORG_GEO_SIMS] = allNames(simJobs);
  }

  const next: GeoJobReadiness = {
    isSettled,
    isError: calcFailed || simFailed || accessFailed,
    retry,
    unreadyJobNames: {
      [ORG_GEO_FIELDS]: namesWithoutData(calcJobs),
      [ORG_GEO_SIMS]: namesWithoutData(simJobs)
    },
    knownJobNames
  };
  // Stringified rather than joined: a job name holding the separator would
  // otherwise read as two jobs, and the stale object would be kept. Both name
  // maps go in the one key, so the guard still hands back a single object.
  const key = JSON.stringify([
    next.isSettled,
    next.isError,
    [next.unreadyJobNames, next.knownJobNames].map((byGroup) =>
      Object.entries(byGroup).map(([group, names]) => [group, [...names].sort()])
    )
  ]);

  if (!resultRef.current || key !== keyRef.current) {
    keyRef.current = key;
    resultRef.current = next;
  }
  return resultRef.current;
}
